import { types, flow, getEnv, SnapshotOut } from 'mobx-state-tree';

import {
    translation,
    //numberFormatter
} from 'easit--localization';
import { ProgressStateModel, ICommonAppEnvWithSBBaseApi } from '../index';
import { ISportsBettingBaseApi, errorIsCancel, TicketListTimeFilterRequest } from '../../api/apiBase';
import { IGroupedApiCtx } from '../../api/apiCommon';
import { ITicketListResponse, EvalState, OfferType, TicketState } from '../../api/proto/apiContract';
import { ListPagerModel } from '../listPager';
import { BaseTicketListStore } from './baseTicketListStore';
import { translationForMts } from '../../../../localization/localization';

export enum TicketListTimeFilterModel { None = "None", All = "All", Day = "Day", ThreeDays = "ThreeDays", Week = "Week", TwoWeeks = "TwoWeeks", Month = "Month" };
export const TicketListTimeFilterModelForStore = types.enumeration('TicketListTimeFilterModelForStore', [TicketListTimeFilterModel.None, TicketListTimeFilterModel.All, TicketListTimeFilterModel.Day, TicketListTimeFilterModel.ThreeDays, TicketListTimeFilterModel.Week, TicketListTimeFilterModel.TwoWeeks, TicketListTimeFilterModel.Month]);

export enum TicketListStateFilterModel { None = "None", Accepted = "Accepted", Win = "Win", Loss = "Loss", Cancel = "Cancel", CancelWithHandlingFee = "CancelWithHandlingFee", Cashout = "Cashout", Storno = "Storno", StornoBySystem = "StornoBySystem" };
export const TicketListStateFilterModelForStore = types.enumeration('TicketListStateFilterModelForStore', [TicketListStateFilterModel.None, TicketListStateFilterModel.Accepted, TicketListStateFilterModel.Win, TicketListStateFilterModel.Loss, TicketListStateFilterModel.Cancel, TicketListStateFilterModel.CancelWithHandlingFee, TicketListStateFilterModel.Cashout, TicketListStateFilterModel.Storno, TicketListStateFilterModel.StornoBySystem]);

const offerTypeNoneKey = -1;

const DefaultTimeFilter = TicketListTimeFilterModel.Month;

export const TicketListStore =
    types.compose(BaseTicketListStore,
        types.model({
            timeFilter: types.optional(TicketListTimeFilterModelForStore, DefaultTimeFilter),
            stateFilter: types.optional(TicketListStateFilterModelForStore, TicketListStateFilterModel.None),
            offerTypeFilter: types.optional(types.number, offerTypeNoneKey), //see OfferType and offerTypeNoneKey
            pager: types.maybeNull(ListPagerModel),
        })).views(_self => {

            const t = translationForMts();

            const timeFilterOptionsPrepared = [
                { key: TicketListTimeFilterModel.None, title: t.ticketList_TimeFilter_None },
                { key: TicketListTimeFilterModel.Day, title: t.ticketList_TimeFilter_Day },
                { key: TicketListTimeFilterModel.ThreeDays, title: t.ticketList_TimeFilter_ThreeDays },
                { key: TicketListTimeFilterModel.Week, title: t.ticketList_TimeFilter_Week },
                { key: TicketListTimeFilterModel.TwoWeeks, title: t.ticketList_TimeFilter_TwoWeeks },
                { key: TicketListTimeFilterModel.Month, title: t.ticketList_TimeFilter_Month }
            ];

            const stateFilterOptionsPrepared = [
                { key: TicketListStateFilterModel.None, title: t.ticketList_StateFilter_None },
                { key: TicketListStateFilterModel.Accepted, title: t.ticketList_StateFilter_Accepted },
                { key: TicketListStateFilterModel.Win, title: t.ticketList_StateFilter_Win },
                { key: TicketListStateFilterModel.Loss, title: t.ticketList_StateFilter_Loss },
                { key: TicketListStateFilterModel.CancelWithHandlingFee, title: t.ticketList_StateFilter_CancelWithHandlingFee },
                { key: TicketListStateFilterModel.Cashout, title: t.ticketList_StateFilter_Cashout },
                { key: TicketListStateFilterModel.Storno, title: t.ticketList_StateFilter_Storno },
            ];

            const offerTypeOptionsPrepared = [
                { key: offerTypeNoneKey, title: t.ticketList_OfferTypeFilter_None },
                { key: OfferType.Prematch, title: t.ticketList_OfferTypeFilter_Prematch },
                { key: OfferType.Live, title: t.ticketList_OfferTypeFilter_Live }];

            return {
                get timeFilterOptions(): { key: TicketListTimeFilterModel, title: string }[] {
                    return timeFilterOptionsPrepared;
                },

                get stateFilterOptions(): { key: TicketListStateFilterModel, title: string }[] {
                    return stateFilterOptionsPrepared;
                },

                get offerTypeFilterOptions(): { key: number, title: string }[] {
                    return offerTypeOptionsPrepared;
                }

            }
        }).actions(self => {

            const env = getEnv<ICommonAppEnvWithSBBaseApi>(self);

            let synchroFullCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
            let synchroNextCtx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
            const ticketsPerPage = 12;

            const loadDataAsync: (isSilent: boolean) => Promise<void> = flow(function* (isSilent: boolean) {

                if (env.loggedUserId <= 0) {
                    self.state = ProgressStateModel.Failed;
                    self.error = translationForMts().myTickets_Login;
                    return;
                }

                let ctx: IGroupedApiCtx<ISportsBettingBaseApi> | null = null;
                try {

                    if (synchroFullCtx && synchroFullCtx.isOpen()) {
                        synchroFullCtx.cancel();
                    }
                    if (synchroNextCtx && synchroNextCtx.isOpen()) {
                        synchroNextCtx.cancel();
                        synchroNextCtx = null;
                    }

                    ctx = env.api.open();
                    synchroFullCtx = ctx;

                    if (!isSilent) {
                        self.state = ProgressStateModel.Loading;
                    }

                    self.error = null;


                    let filterForApi = prepareFilterForApi();
                    filterForApi.rowsOfPage = ticketsPerPage;
                    filterForApi.indexOfPage = self.pager.currentPage;

                    const response: ITicketListResponse = yield ctx.api.getTicketListAsync(filterForApi);

                    self.totalTicketsCount = response.TotalCount;
                    self.setTickets(response.Items);
                    preparePager();

                    self.state = ProgressStateModel.Success;
                } catch (error) {
                    if (errorIsCancel(error)) {
                        throw error;
                    }
                    env.loggingService.error(error);
                    self.state = ProgressStateModel.Failed;
                    self.error = translation().bet_ErrorInTicketListLoad;
                    throw error;
                } finally {
                    if (synchroFullCtx === ctx) {
                        synchroFullCtx = null;
                    }
                }
            });

            const preparePager = (): void => {
                const currentPage = self.pager.currentPage;
                const totalPages = Math.ceil(self.totalTicketsCount / ticketsPerPage);
                const pageStart = Math.max(currentPage - 3, 1);
                const pagesEnd = Math.min(currentPage + 3, totalPages);
                self.pager = ListPagerModel.create(<SnapshotOut<typeof ListPagerModel>>{
                    currentPage: currentPage,
                    pagesStart: pageStart,
                    pagesEnd: pagesEnd,
                    totalPages: totalPages
                });
            }

            const setFilterAsync = (timeFilter: TicketListTimeFilterModel, state: TicketListStateFilterModel, offerType: number /* OfferType plus offerTypeNoneKey */): Promise<void> => {
                setFilterNoLoad(timeFilter, state, offerType);
                return loadDataAsync(false);
            }

            const setFilterNoLoad = (timeFilter: TicketListTimeFilterModel, state: TicketListStateFilterModel, offerType: number /* OfferType plus offerTypeNoneKey */) => {
                self.timeFilter = timeFilter;
                self.stateFilter = state;
                self.offerTypeFilter = offerType;
                self.pager = ListPagerModel.create(<SnapshotOut<typeof ListPagerModel>>{});
                self.isLastActionSetFilter = true;
            }

            const resetFilter = (state: TicketListStateFilterModel): void => {
                setFilterNoLoad(DefaultTimeFilter, state, offerTypeNoneKey);
            }

            const prepareFilterForApi = (): any => {
                let timeFilter: TicketListTimeFilterRequest | undefined;
                let evalState: number | undefined;
                let ticketState: number | undefined;
                let offerType: OfferType | undefined;

                if (self.timeFilter) {
                    switch (self.timeFilter) {
                        case TicketListTimeFilterModel.None:
                        case TicketListTimeFilterModel.All: timeFilter = undefined; break; //ignore
                        case TicketListTimeFilterModel.Day: timeFilter = TicketListTimeFilterRequest.Day; break;
                        case TicketListTimeFilterModel.ThreeDays: timeFilter = TicketListTimeFilterRequest.ThreeDays; break;
                        case TicketListTimeFilterModel.Week: timeFilter = TicketListTimeFilterRequest.Week; break;
                        case TicketListTimeFilterModel.TwoWeeks: timeFilter = TicketListTimeFilterRequest.TwoWeeks; break;
                        case TicketListTimeFilterModel.Month: timeFilter = TicketListTimeFilterRequest.Month; break;
                        default: throw new Error("Unexpected time filter " + self.timeFilter);
                    }
                } else {
                    timeFilter = undefined;
                }

                if (self.stateFilter) {
                    switch (self.stateFilter) {
                        case TicketListStateFilterModel.None: ticketState = undefined; evalState = undefined; break;
                        case TicketListStateFilterModel.Accepted: ticketState = TicketState.Accepted; evalState = undefined; break;
                        case TicketListStateFilterModel.Win: ticketState = TicketState.Evaluated; evalState = EvalState.Win; break;
                        case TicketListStateFilterModel.Loss: ticketState = TicketState.Evaluated; evalState = EvalState.Loss; break;
                        case TicketListStateFilterModel.Cancel: ticketState = TicketState.Evaluated; evalState = EvalState.Cancel; break;
                        case TicketListStateFilterModel.CancelWithHandlingFee: ticketState = TicketState.Evaluated; evalState = EvalState.CancelWithHandlingFee; break;
                        case TicketListStateFilterModel.Cashout: ticketState = TicketState.Evaluated; evalState = EvalState.Cashout; break;
                        case TicketListStateFilterModel.Storno: ticketState = TicketState.Storno; evalState = undefined; break;
                        case TicketListStateFilterModel.StornoBySystem: ticketState = TicketState.StornoBySystem; evalState = undefined; break;
                        default: throw new Error("Unexpected state filter " + self.stateFilter);
                    }
                } else {
                    evalState = undefined;
                    ticketState = undefined;
                }

                if (self.offerTypeFilter >= 0) { //see OfferType
                    offerType = self.offerTypeFilter;
                } else {
                    offerType = undefined;
                }

                return {
                    timeFilter: timeFilter,
                    ticketState: ticketState,
                    evalState: evalState,
                    offerType: offerType
                };
            }

            const setPagerPageAsync = (pageIndex: number): Promise<void> => {
                self.pager.setCurrentPage(pageIndex);
                self.isLastActionSetFilter = false;
                return loadDataAsync(false);
            }

            return {
                loadDataAsync,
                setPagerPageAsync,
                setFilterAsync,
                resetFilter
            }
        });


